// @givmo/ai — a zero-dep incremental SSE state machine. Mirrors iOS
// `AssistantSSEParser.swift`. `consume(chunk)` is fed raw text and returns the
// `RawSseEvent`s completed in that chunk. Decoding a `data:` payload to a typed
// event is deliberately the CLIENT's job (the stream client), not the parser's,
// so a malformed payload is skipped without crashing.
//
// Per the W3C EventStream parsing rules + the Givmo contract (§1.1):
//   - lines are split on `\n`, `\r`, or `\r\n` (CRLF-tolerant);
//   - a `:`-prefixed line is a COMMENT → discarded (handles the 2048-byte first-
//     delivery pad + the 11s ping the backend emits; clients MUST ignore comments);
//   - `id:`/`event:`/`data:` accumulate into the in-progress event; a leading
//     space after the colon is stripped (per spec); multiple `data:` lines join
//     with `\n`;
//   - a BLANK line dispatches the buffered event;
//   - a field with no value (no colon) is treated as the field name with an empty
//     value (per spec — e.g. a bare `data`).

/** A completed SSE record (the envelope), before payload decoding. */
export interface RawSseEvent {
  /** The `id:` value (`{turn_id}:{attempt}:{seq}` — the `Last-Event-ID` resume
   * key), or `undefined` if the event carried none. */
  id?: string;
  /** The `event:` name (the §1.2 vocabulary), or `undefined` (defaults to
   * `message` per spec — the contract always names the event). */
  event?: string;
  /** The joined `data:` payload (raw JSON text). */
  data: string;
}

export class SseParser {
  private buffer = '';
  private dataLines: string[] = [];
  private eventName: string | undefined;
  private lastId: string | undefined;
  private sawData = false;

  /** Feed a raw text chunk; returns every event completed within it. Partial
   * trailing lines are retained across calls. */
  consume(chunk: string): RawSseEvent[] {
    this.buffer += chunk;
    const events: RawSseEvent[] = [];

    // Split on CRLF / CR / LF, keeping the trailing partial line in `buffer`.
    // Normalize CRLF → LF and lone CR → LF first so a single split handles all.
    const normalized = this.buffer.replace(/\r\n?/g, '\n');
    const segments = normalized.split('\n');
    // The last segment is an incomplete line (no terminator yet) — keep it.
    this.buffer = segments.pop() ?? '';

    for (const line of segments) {
      const dispatched = this.handleLine(line);
      if (dispatched) events.push(dispatched);
    }
    return events;
  }

  /** Process one complete line; returns a dispatched event on a blank line. */
  private handleLine(line: string): RawSseEvent | undefined {
    // Blank line → dispatch the buffered event (if any field accumulated).
    if (line === '') {
      return this.dispatch();
    }

    // Comment line (`:` first char) → discard (pad + ping).
    if (line.startsWith(':')) {
      return undefined;
    }

    const colon = line.indexOf(':');
    let field: string;
    let value: string;
    if (colon === -1) {
      // No colon → whole line is the field name with an empty value.
      field = line;
      value = '';
    } else {
      field = line.slice(0, colon);
      value = line.slice(colon + 1);
      // Strip a single leading space after the colon (per spec).
      if (value.startsWith(' ')) value = value.slice(1);
    }

    switch (field) {
      case 'id':
        // A null byte in id is ignored per spec; otherwise set the last id.
        if (!value.includes(' ')) this.lastId = value;
        break;
      case 'event':
        this.eventName = value;
        break;
      case 'data':
        this.dataLines.push(value);
        this.sawData = true;
        break;
      // `retry` and unknown fields are ignored (the contract sets retry on the
      // client side, not via the stream).
      default:
        break;
    }
    return undefined;
  }

  /** Build + reset the in-progress event. Returns `undefined` when nothing has
   * accumulated (consecutive blank lines / leading pad). */
  private dispatch(): RawSseEvent | undefined {
    if (!this.sawData && this.eventName === undefined && this.lastId === undefined) {
      return undefined;
    }
    // Per spec, if no data field fired, the event is NOT dispatched — but an
    // `id`-only or `event`-only block can still update the last id. We only emit
    // a record when data was present; otherwise we still keep `lastId` (already
    // stored above) and reset the per-event scratch.
    const data = this.dataLines.join('\n');
    const sawData = this.sawData;
    const event: RawSseEvent = {
      id: this.lastId,
      event: this.eventName,
      data,
    };
    // Reset per-event scratch (lastId persists as the EventStream "last event ID").
    this.dataLines = [];
    this.eventName = undefined;
    this.sawData = false;
    return sawData ? event : undefined;
  }

  /** The current last-seen `id:` (the resume cursor). Persists across events. */
  get lastEventId(): string | undefined {
    return this.lastId;
  }

  /** Seed the resume cursor (e.g. on reconnect with a known `Last-Event-ID`). */
  set lastEventId(value: string | undefined) {
    this.lastId = value;
  }
}
